'use client'

import UIFlex from '@components/ui/UIFlex'
import UILink from '@components/ui/UILink'
import UIText from '@components/ui/UIText'
import color from '@ui/style/color.theme'
import { pxToRem } from '@ui/style/muiTheme'
import { useContext } from 'react'
import { Icon16Time } from 'src/assets/icons/Icon16Time'
import { ArticleRefContext } from '@page/article/_components/common/ArticleRefContext'

interface TitleBlockProps {
	title: string
	mobileTitle?: string
	textContent?: string
	sectionName?: string
	sectionHref?: string
	packageName?: string
	packageHref?: string
	createdAt: string
	updatedAt?: string
	isDark?: boolean
}
const TitleBlock = ({
	title,
	mobileTitle,
	textContent,
	sectionName,
	sectionHref,
	packageName,
	packageHref,
	createdAt,
	updatedAt,
	isDark,
}: TitleBlockProps) => {
	const articleRef = useContext(ArticleRefContext)
	const estimatedReadTime = (content?: string) => {
		const readWordPerMinute = 250
		const wordSize = content?.split(/\s+/).filter(Boolean).length ?? 0
		return wordSize <= readWordPerMinute
			? '1분 이내'
			: `${Math.ceil(wordSize / readWordPerMinute)}분`
	}

	return (
		<UIFlex
			sx={{
				'&>*:not(:first-child)': {
					marginTop: {
						xs: pxToRem(12),
						md: pxToRem(16),
					},
				},
			}}
		>
			{/* 분야, 특집 패키지 이름 */}
			<UIFlex
				flexDirection="row"
				sx={{
					'&>*:not(:first-child)': {
						marginLeft: pxToRem(8),
					},
				}}
			>
				{sectionName && (
					<UIText
						fontSize={{ xs: pxToRem(16), md: pxToRem(18), lg: pxToRem(20) }}
						lineHeight={{ xs: pxToRem(16), md: pxToRem(18), lg: pxToRem(20) }}
						fontWeight={700}
						color={isDark ? color.colWhite : color.colDarkBlue}
						sx={{
							'& .MuiLink-root': {
								fontSize: 'inherit',
								lineHeight: 'inherit',
							},
						}}
					>
						<UILink href={sectionHref}>{sectionName}</UILink>
					</UIText>
				)}
				{packageName && (
					<UIText
						fontSize={{ xs: pxToRem(16), md: pxToRem(18), lg: pxToRem(20) }}
						lineHeight={{ xs: pxToRem(16), md: pxToRem(18), lg: pxToRem(20) }}
						fontWeight={600}
						color={isDark ? color.colWhite : color.colDarkBlue}
						sx={{
							opacity: 0.6,
							'& .MuiLink-root': {
								fontSize: 'inherit',
								lineHeight: 'inherit',
							},
						}}
					>
						{packageHref ? (
							<UILink href={packageHref}>{packageName}</UILink>
						) : (
							packageName
						)}
					</UIText>
				)}
			</UIFlex>
			{/* 타이틀 */}
			<UIText
				level="ArticleTitle"
				color={isDark ? color.colWhite : color.colBlack}
				ref={articleRef.titleRef}
			>
				<UIText
					component="span"
					color={isDark ? color.colWhite : color.colBlack}
					sx={mobileTitle ? { display: { xs: 'none', md: 'inline' } } : {}}
				>
					{title}
				</UIText>
				{mobileTitle && (
					<UIText
						component="span"
						color={isDark ? color.colWhite : color.colBlack}
						sx={{ display: { xs: 'inline', md: 'none' } }}
					>
						{mobileTitle}
					</UIText>
				)}
			</UIText>
			<UIFlex
				component={UIText}
				flexDirection="row"
				flexWrap="wrap"
				mb={pxToRem(-8)}
				sx={{
					'.MuiTypography-root': {
						color: isDark ? 'rgba(255,255,255,0.6)' : color.colGray5,
						fontSize: pxToRem(14),
						lineHeight: pxToRem(16),
					},
				}}
			>
				{/* 에상 기사 소비 시간 */}
				<UIFlex
					component={UIText}
					flexDirection="row"
					alignItems="center"
					mr={pxToRem(12)}
					mb={pxToRem(8)}
					sx={{
						svg: {
							marginRight: pxToRem(2),
						},
					}}
				>
					<Icon16Time isDark={isDark} />
					{textContent && estimatedReadTime(textContent)}
				</UIFlex>
				{/* 입력 /  수정 시간 */}
				<UIFlex
					component={UIText}
					flexDirection="row"
					flexWrap="wrap"
					mb={pxToRem(8)}
					sx={{
						'&>*:not(:first-child)': {
							marginLeft: '0.5em',
						},
					}}
				>
					<UIText component="span">입력 {createdAt}</UIText>
					{updatedAt && createdAt !== updatedAt && (
						<UIText component="span">수정 {updatedAt}</UIText>
					)}
				</UIFlex>
			</UIFlex>
		</UIFlex>
	)
}

export default TitleBlock
